/**
 * Deduplication registry.
 *
 * Two different jobs:
 *  - stop a collector firing the same event twice in one session (view_content on
 *    every scroll, begin_checkout on every refresh);
 *  - give the client and the server the same `dedupe_key` for events both send, so a
 *    downstream query can collapse them and raise `duplicate_event_fired` (spec §3)
 *    when they do not collapse.
 *
 * Purchase keys live in localStorage because a thank-you page is often reopened days
 * later from the confirmation e-mail; everything else is session-scoped.
 */

export const SCOPE_SESSION = 'session';
export const SCOPE_PERSISTENT = 'persistent';

const PREFIX = 'td_dedupe:';
const PERSISTENT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function safeStorage(factory) {
  try {
    const storage = factory();
    const probe = PREFIX + 'probe';
    storage.setItem(probe, '1');
    storage.removeItem(probe);

    return storage;
  } catch (e) {
    return null;
  }
}

export class DedupRegistry {
  constructor({
    session = () => window.sessionStorage,
    persistent = () => window.localStorage,
    now = () => Date.now(),
  } = {}) {
    this.now = now;
    this.stores = {
      [SCOPE_SESSION]: safeStorage(session),
      [SCOPE_PERSISTENT]: safeStorage(persistent),
    };
    this.memory = new Map();
  }

  /**
   * True the first time a key is seen; false on every repeat.
   */
  once(key, scope = SCOPE_SESSION) {
    if (!key) {
      return true;
    }

    if (this.seen(key, scope)) {
      return false;
    }

    this.remember(key, scope);

    return true;
  }

  seen(key, scope = SCOPE_SESSION) {
    const stamp = this.read(key, scope);

    if (stamp === null) {
      return false;
    }

    if (scope === SCOPE_PERSISTENT && this.now() - stamp > PERSISTENT_TTL_MS) {
      return false;
    }

    return true;
  }

  remember(key, scope = SCOPE_SESSION) {
    const store = this.stores[scope];
    const value = String(this.now());

    if (store) {
      try {
        store.setItem(PREFIX + key, value);
        return;
      } catch (e) {
        /* quota or private mode — fall through to memory */
      }
    }

    this.memory.set(scope + ':' + key, this.now());
  }

  read(key, scope) {
    const store = this.stores[scope];

    if (store) {
      try {
        const raw = store.getItem(PREFIX + key);
        if (raw !== null) {
          const stamp = parseInt(raw, 10);

          return Number.isNaN(stamp) ? this.now() : stamp;
        }
      } catch (e) {
        /* fall through to memory */
      }
    }

    const fromMemory = this.memory.get(scope + ':' + key);

    return fromMemory === undefined ? null : fromMemory;
  }
}
